import type { ClientDirectory, ClientIntegrationType, ClientWithIntegrations } from '../../clients/model.js';
import { can } from '../../authorization/policy.js';
import type { AuthenticatedUser } from '../auth/model.js';
import { escapeAttribute, escapeHtml } from '../html.js';
import type { MspHealthPrototypeModel, PrototypeClientCard } from '../model.js';
import { renderAuthenticatedShell } from '../view.js';
import type { ClientSectionId } from '../settings/routing.js';
import type { ClientOnboardingConfiguration } from '../../onboarding/model.js';
import { renderClientOnboarding } from './onboarding-view.js';

const INTEGRATION_LABELS: Readonly<Record<ClientIntegrationType, string>> = {
  NINJAONE: 'NinjaOne',
  MICROSOFT_365: 'Microsoft 365',
  IT_GLUE: 'IT Glue',
  THREECX: '3CX',
  SIP_PROVIDER: 'SIP Provider',
  OPENPROJECT: 'OpenProject',
};

const DETAIL_SECTIONS: readonly { id: ClientSectionId; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'backups', label: 'Backups' },
  { id: 'm365', label: 'Microsoft 365' },
  { id: '3cx', label: '3CX' },
  { id: 'pc-health', label: 'PC Health' },
  { id: 'onboarding', label: 'Onboarding' },
  { id: 'projects', label: 'Projects' },
];

export function renderClientDirectory(
  directory: ClientDirectory,
  dashboard: MspHealthPrototypeModel,
  user: AuthenticatedUser,
  feedback = '',
): string {
  const canManage = can(user.roles, 'MANAGE_CLIENTS');
  return renderAuthenticatedShell(user, directory.clients.length, 'CLIENTS', `
    <main class="workspace" data-view="clients">
      <header class="page-heading client-page-heading">
        <div><h1>Clients</h1><p>Managed clients and connected operational sources</p></div>
        ${canManage ? '<button type="button" class="client-primary-action" data-new-client-toggle>+ New Client</button>' : ''}
      </header>
      ${feedback ? `<p class="client-feedback" role="status">${escapeHtml(feedback)}</p>` : ''}
      ${canManage ? renderNewClientForm() : ''}
      <section class="content-section client-directory" aria-labelledby="managed-clients-title">
        <div class="section-title"><h2 id="managed-clients-title">Managed Clients</h2><span>${directory.clients.length} clients</span></div>
        <label class="client-search-label">Search clients
          <input type="search" data-client-search placeholder="Search by client name" aria-label="Search by client name">
        </label>
        <div class="client-grid">
          ${directory.clients.map((client) => renderDirectoryCard(client, dashboard)).join('')}
        </div>
        <p class="client-search-empty" data-client-search-empty hidden>No matching clients.</p>
      </section>
      ${directory.unlinkedNinjaOrganizations.length > 0 ? renderUnlinkedOrganizations(directory, canManage) : ''}
    </main>
  `);
}

export function renderClientDirectoryLoading(
  user: AuthenticatedUser,
  managedClientCount: number,
  message: string,
): string {
  return renderAuthenticatedShell(user, managedClientCount, 'CLIENTS', `
    <main class="workspace" data-view="clients"><header class="page-heading"><h1>Clients</h1></header>
      <p role="status">${escapeHtml(message)}</p></main>
  `);
}

export function renderClientDetail(
  client: ClientWithIntegrations | null,
  dashboard: MspHealthPrototypeModel,
  user: AuthenticatedUser,
  feedback = '',
  section: ClientSectionId | null = null,
  onboarding?: ClientOnboardingConfiguration,
  rawOnboardingUrl: string | null = null,
): string {
  if (client === null) {
    return renderAuthenticatedShell(user, dashboard.managedClientCount, 'CLIENTS', `
      <main class="workspace" data-view="client-detail"><a href="/clients" data-app-route>← Clients</a>
        <h1>Client unavailable</h1><p>The client could not be found.</p></main>
    `);
  }
  const ninjaLinks = client.integrations.filter((integration) => integration.type === 'NINJAONE');
  const backupCards = ninjaLinks.flatMap((integration) => {
    const organizationId = Number(integration.externalId);
    return dashboard.clients.filter((card) => card.organizationId === organizationId);
  });
  const selectedSection = section ?? 'overview';
  const clientPath = `/clients/${encodeURIComponent(client.id)}`;
  return renderAuthenticatedShell(user, dashboard.managedClientCount, 'CLIENTS', `
    <main class="workspace client-detail" data-view="client-detail" data-client-detail-id="${escapeAttribute(client.id)}">
      <a class="client-back-link" href="/clients" data-app-route>← Clients</a>
      <header class="page-heading"><h1>${escapeHtml(client.name)}</h1>
        <p>${client.active ? 'Active client' : 'Inactive client'} · ${escapeHtml(client.slug)}</p></header>
      ${feedback ? `<p class="client-feedback" role="status">${escapeHtml(feedback)}</p>` : ''}
      <nav class="client-detail-nav" aria-label="Client sections">
        ${DETAIL_SECTIONS.map((item) => `<a href="${clientPath}${item.id === 'overview' ? '' : `/${item.id}`}" data-app-route data-client-detail-tab="${escapeAttribute(item.label)}"${selectedSection === item.id ? ' class="active" aria-current="page"' : ''}>${escapeHtml(item.label)}</a>`).join('')}
      </nav>
      ${selectedSection === 'overview' ? `<section data-client-detail-section="Overview" class="content-section">
        <div class="section-title"><h2>Overview</h2></div>
        <p>${ninjaLinks.length} linked NinjaOne ${ninjaLinks.length === 1 ? 'organization' : 'organizations'}.</p>
        <p>${backupCards.reduce((count, card) => count + card.monitoredDeviceCount, 0)} monitored Backup devices. Open Backups for organization-level health.</p>
      </section>` : ''}
      ${selectedSection === 'backups' ? `<section data-client-detail-section="Backups" class="content-section">
        <div class="section-title"><h2>Backups</h2></div>
        ${backupCards.length > 0 ? `<div class="client-integration-list">${backupCards.map((card) => `<article class="client-integration-row"><div><strong>${escapeHtml(card.clientName)}</strong><small>NinjaOne organization ID ${card.organizationId}</small></div><div><span>Observed Backup Health ${card.backupScore === null ? '—' : `${card.backupScore}%`}</span><small>${card.monitoredDeviceCount} monitored devices</small></div></article>`).join('')}</div>` : ninjaLinks.length === 0 ? '<p>No linked NinjaOne backup data yet.</p>' : '<p>No backup monitoring configured for this client.</p>'}
        <p><a href="/backups" data-app-route>Open Backup Health</a> for current exceptions and destination coverage.</p>
      </section>` : ''}
      ${selectedSection === 'integrations' ? `<section data-client-detail-section="Integrations" class="content-section">
        <div class="section-title"><h2>Integrations</h2></div>
        <p>External systems linked to this client. Connection management is available as each connector is implemented; operational health appears in its own module.</p>
        <div class="client-integration-list">${(Object.keys(INTEGRATION_LABELS) as ClientIntegrationType[]).map(
          (type) => renderIntegration(type, client),
        ).join('')}</div>
      </section>` : ''}
      ${selectedSection === 'm365' ? renderClientMicrosoft365(client, can(user.roles, 'MANAGE_CLIENTS'), clientPath) : ''}
      ${selectedSection === 'onboarding' ? renderClientOnboarding(onboarding, can(user.roles, 'MANAGE_CLIENTS'), rawOnboardingUrl) : ''}
      ${selectedSection !== 'overview' && selectedSection !== 'integrations' && selectedSection !== 'm365' && selectedSection !== 'backups' && selectedSection !== 'onboarding'
        ? `<section data-client-detail-section="${escapeAttribute(selectedSection)}" class="content-section">
          <div class="section-title"><h2>${escapeHtml(DETAIL_SECTIONS.find((item) => item.id === selectedSection)?.label ?? selectedSection)}</h2></div>
          <p>${escapeHtml(placeholderDescription(selectedSection))}</p>
        </section>` : ''}
    </main>
  `);
}

function renderDirectoryCard(client: ClientWithIntegrations, dashboard: MspHealthPrototypeModel): string {
  const links = client.integrations.filter((integration) => integration.type === 'NINJAONE');
  const backupCard: PrototypeClientCard | undefined = links.length === 1
    ? dashboard.clients.find((card) => card.organizationId === Number(links[0]?.externalId))
    : undefined;
  const score = backupCard === undefined || backupCard.backupScore === null ? '—' : `${backupCard.backupScore}%`;
  return `<a class="client-card managed-client-card ${backupCard === undefined ? 'tone-neutral' : `tone-${backupCard.tone.toLowerCase()}`} internal-client-card"
      href="/clients/${encodeURIComponent(client.id)}" data-app-route data-client-search-name="${escapeAttribute(client.name.toLowerCase())}">
      <span class="client-head"><span><strong>${escapeHtml(client.name)}</strong><small>${links.length} linked NinjaOne ${links.length === 1 ? 'organization' : 'organizations'}</small></span>
        <span class="client-score" title="Observed Backup Health"><i aria-hidden="true"></i><strong>${score}</strong></span></span>
      <span class="client-domain-list">
        <span><b>Backups</b><small>${backupCard === undefined ? links.length === 1 ? 'No backup monitoring' : 'No single-org score' : `${backupCard.monitoredDeviceCount} monitored devices`}</small></span>
        <span><b>Microsoft 365</b><small>${integrationSummary(client, 'MICROSOFT_365')}</small></span>
        <span><b>IT Glue</b><small>${integrationSummary(client, 'IT_GLUE')}</small></span>
      </span>
      <span class="client-entry-label">Open client</span>
    </a>`;
}

function renderNewClientForm(): string {
  return `<form class="client-form content-section" data-new-client-form hidden>
    <h2>New Client</h2>
    <label>Client name<input name="name" required maxlength="160" autocomplete="off"></label>
    <label>Slug<input name="slug" required maxlength="64" pattern="[a-z0-9]+(-[a-z0-9]+)*" autocomplete="off"></label>
    <label class="client-checkbox"><input type="checkbox" name="active" checked> Active</label>
    <button type="submit" class="client-primary-action">Create Client</button>
  </form>`;
}

function renderUnlinkedOrganizations(directory: ClientDirectory, canManage: boolean): string {
  return `<section class="content-section client-unlinked" aria-labelledby="unlinked-ninja-title">
    <div class="section-title"><h2 id="unlinked-ninja-title">Unlinked Ninja Organizations · Needs Review</h2>
      <span>${directory.unlinkedNinjaOrganizations.length} organizations</span></div>
    ${directory.unlinkedNinjaOrganizations.map((organization) => `
        <div class="client-unlinked-row">
          <span><strong>${escapeHtml(organization.organizationName)}</strong><small>Ninja organization ID ${organization.organizationId}</small></span>
          ${canManage ? `<span class="client-unlinked-actions">
            <button type="button" data-create-from-ninja="${organization.organizationId}">Create Client from Organization</button>
            <form data-link-ninja-form data-organization-id="${organization.organizationId}">
              <select name="clientId" aria-label="Existing client for ${escapeAttribute(organization.organizationName)}" required>
                <option value="">Select existing client</option>
                ${directory.clients.map((client) => `<option value="${escapeAttribute(client.id)}">${escapeHtml(client.name)}</option>`).join('')}
              </select>
              <button type="submit"${directory.clients.length === 0 ? ' disabled' : ''}>Link to Existing Client</button>
            </form></span>` : ''}
        </div>`).join('')}
  </section>`;
}

function renderIntegration(type: ClientIntegrationType, client: ClientWithIntegrations): string {
  const links = client.integrations.filter((integration) => integration.type === type);
  const description = type === 'NINJAONE' ? 'Operational and backup discovery source'
    : type === 'IT_GLUE' ? 'Documentation and integration workflow; not a Health score source'
      : 'Not connected';
  return `<article class="client-integration-row"><div><strong>${INTEGRATION_LABELS[type]}</strong>
    <small>${description}</small></div><div>
    <span>${links.length === 0 ? 'Not connected' : links.some((link) => link.status === 'NEEDS_ATTENTION') ? 'Needs attention' : 'Connected'}</span>
    ${links.map((link) => `<small>${escapeHtml(link.displayName)} · ID ${escapeHtml(link.externalId)}${link.lastVerifiedAt ? ` · Last verified ${escapeHtml(link.lastVerifiedAt)}` : ''}</small>`).join('')}
  </div></article>`;
}

function integrationSummary(client: ClientWithIntegrations, type: ClientIntegrationType): string {
  const links = client.integrations.filter((integration) => integration.type === type);
  return links.length === 0 ? 'Not connected' : links.some((link) => link.status === 'NEEDS_ATTENTION')
    ? 'Needs attention' : 'Connected';
}

function renderClientMicrosoft365(client: ClientWithIntegrations, canManage: boolean, clientPath: string): string {
  const status = integrationSummary(client, 'MICROSOFT_365');
  return `<section data-client-detail-section="Microsoft 365" class="content-section">
    <div class="section-title"><h2>Microsoft 365</h2></div>
    <p>${escapeHtml(status)}</p>
    <p>Service health, incidents, and licensing for this client are not available until the Microsoft 365 collector is connected.</p>
    ${canManage ? `<a href="${clientPath}/integrations" data-app-route>View Client Integrations</a>` : ''}
  </section>`;
}

function placeholderDescription(section: ClientSectionId): string {
  const descriptions: Partial<Record<ClientSectionId, string>> = {
    '3cx': '3CX operations for this client are planned and not connected.',
    'pc-health': 'PC health for this client is planned and not connected.',
    onboarding: 'Onboarding workflow for this client is planned.',
    projects: 'Projects for this client are planned and not connected.',
  };
  return descriptions[section] ?? 'This client area is planned.';
}
